import { useState, useEffect } from 'react';
import { bondsApi } from '../services/api';
import type { Bond } from '../types/bonds';

export function useSaveBonds() {
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);

  const saveBonds = async (bonds: Bond[], fileName?: string, refetch?: () => void, onPageRefresh?: () => void): Promise<Bond[] | null> => {
    // Reset states
    setSaveError(null);
    setSaveSuccess(null);
    setIsSaving(true);

    try {
      const updatedBonds = await bondsApi.updateBonds(bonds, fileName);
      setSaveSuccess(`Pomyślnie zapisano - strona zostanie odświeżona za chwilę`);

      // Refetch data after successful save to ensure we have the latest data from server
      if (refetch) {
        refetch();
      }

      // Call page refresh callback if provided (for comprehensive page refresh)
      // Add a small delay to ensure user sees the success message
      if (onPageRefresh) {
        setTimeout(() => {
          onPageRefresh();
        }, 1500); // 1.5 second delay
      }

      return updatedBonds;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Wystąpił błąd podczas zapisywania danych';
      setSaveError(errorMessage);
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  // Clear notifications after 3 seconds
  useEffect(() => {
    if (saveSuccess || saveError) {
      const timer = setTimeout(() => {
        setSaveSuccess(null);
        setSaveError(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [saveSuccess, saveError]);

  const resetSaveState = () => {
    setSaveError(null);
    setSaveSuccess(null);
  };

  return {
    saveBonds,
    isSaving,
    saveError,
    saveSuccess,
    resetSaveState
  };
} 